import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import aiExtractionService, { ExtractionJob } from '../services/aiExtractionService';
import notify from '../lib/notify';

interface UseAIExtractionReturn {
  jobs: ExtractionJob[];
  selectedJob: ExtractionJob | null;
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  uploadDocument: (fileOrText: File | string, requestedAction?: string, context?: Record<string, unknown>) => Promise<void>;
  fetchJobs: () => Promise<void>;
  selectJob: (jobId: string) => Promise<void>;
  approveJob: (jobId: string, approvalData?: { modified_preparation_result?: unknown }) => Promise<ExtractionJob>;
  rejectJob: (jobId: string) => Promise<void>;
  deleteAllJobs: () => Promise<void>;
  clearError: () => void;
}

export function useAIExtraction(): UseAIExtractionReturn {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<ExtractionJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<ExtractionJob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await aiExtractionService.listJobs({ limit: 100 });
      setJobs(data);

      // Auto-update selectedJob if it exists in the new data
      setSelectedJob(prevSelected => {
        if (!prevSelected) return null;
        const updatedJob = data.find(j => j.id === prevSelected.id);
        return updatedJob || prevSelected;
      });
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail || (err as Error).message || t('aiExtraction.errors.fetchJobs');
      setError(errorMsg);
      notify.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const uploadDocument = useCallback(async (
    fileOrText: File | string,
    requestedAction: string = 'auto',
    context: Record<string, unknown> = {}
  ) => {
    setIsUploading(true);
    setError(null);
    try {
      const newJob = await aiExtractionService.uploadDocument(fileOrText, requestedAction, context);
      const fileName = fileOrText instanceof File ? fileOrText.name : t('aiChatbot.documentDefaultTitle');
      notify.show({
        variant: 'success',
        title: t('aiExtraction.toasts.uploadedTitle'),
        description: t('aiExtraction.toasts.uploadedDescription', { name: fileName }),
        link: '/ai-extraction',
      });
      // Refresh jobs list
      await fetchJobs();
      // Auto-select the new job
      setSelectedJob(newJob);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | Record<string, unknown> }; status?: number }; message?: string };
      const detail = e?.response?.data?.detail;
      const isQuotaExceeded =
        e?.response?.status === 403 &&
        typeof detail === 'object' &&
        detail !== null &&
        (detail as Record<string, unknown>)['code'] === 'org_quota_exceeded';
      const errorMsg = isQuotaExceeded
        ? t('genieArtifact.quotaBlocked.message')
        : ((typeof detail === 'string' ? detail : null) || (err as Error).message || t('aiExtraction.errors.upload'));
      setError(errorMsg);
      notify.error(isQuotaExceeded ? t('genieArtifact.quotaBlocked.title') : errorMsg);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, [fetchJobs, t]);

  const selectJob = useCallback(async (jobId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const job = await aiExtractionService.getJob(jobId);
      setSelectedJob(job);
      // Update job in list if it exists
      setJobs(prev => prev.map(j => j.id === jobId ? job : j));
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail || (err as Error).message || t('aiExtraction.errors.fetchJob');
      setError(errorMsg);
      notify.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const approveJob = useCallback(async (jobId: string, approvalData?: { modified_preparation_result?: unknown }) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedJob = await aiExtractionService.approveJob(jobId, approvalData);

      const action = updatedJob.requested_action;
      const result = (updatedJob.extraction_result as Record<string, string> | undefined) ?? {};
      const ctx = (updatedJob.context as Record<string, string> | undefined) ?? {};
      let entityLink: string = '/ai-extraction';

      if (action === 'create_usecase' || action === 'update_usecase') {
        const id = result.usecase_id ?? ctx.usecase_id;
        if (id) entityLink = `/cas-usage/${id}`;
      } else if (action === 'create_cadrage' || action === 'update_cadrage') {
        const cadrageId = result.cadrage_id ?? ctx.cadrage_id;
        const usecaseId = result.usecase_id ?? ctx.usecase_id;
        if (cadrageId) entityLink = `/cadrages/${cadrageId}`;
        else if (usecaseId) entityLink = `/cas-usage/${usecaseId}`;
      }

      notify.show({
        variant: 'success',
        title: t('aiExtraction.toasts.approvedTitle'),
        description: t('aiExtraction.toasts.approvedDescription'),
        link: entityLink,
      });
      // Update jobs list
      setJobs(prev => prev.map(j => j.id === jobId ? updatedJob : j));
      setSelectedJob(updatedJob);
      return updatedJob; // Return the updated job for navigation purposes
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail || (err as Error).message || t('aiExtraction.errors.approve');
      setError(errorMsg);
      notify.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const rejectJob = useCallback(async (jobId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedJob = await aiExtractionService.rejectJob(jobId);
      notify.show({
        variant: 'warning',
        title: t('aiExtraction.toasts.rejectedTitle'),
        description: t('aiExtraction.toasts.rejectedDescription'),
        link: '/ai-extraction',
      });
      // Update jobs list
      setJobs(prev => prev.map(j => j.id === jobId ? updatedJob : j));
      setSelectedJob(updatedJob);
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail || (err as Error).message || t('aiExtraction.errors.reject');
      setError(errorMsg);
      notify.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const deleteAllJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await aiExtractionService.deleteAllJobs();
      notify.success(result.message);
      // Clear jobs list and selected job
      setJobs([]);
      setSelectedJob(null);
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail || (err as Error).message || t('aiExtraction.errors.deleteJobs');
      setError(errorMsg);
      notify.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-refresh jobs periodically if there are pending/processing jobs
  useEffect(() => {
    // Initial fetch
    fetchJobs();
  }, [fetchJobs]);

  // Polling effect - check for active jobs and adjust polling
  useEffect(() => {
    const hasActiveJobs = jobs.some(j => j.status === 'pending' || j.status === 'processing');

    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only start polling if there are active jobs
    if (hasActiveJobs) {
      // Poll every 5 seconds for faster feedback when job completes
      pollingIntervalRef.current = setInterval(() => {
        fetchJobs();
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  return {
    jobs,
    selectedJob,
    isLoading,
    isUploading,
    error,
    uploadDocument,
    fetchJobs,
    selectJob,
    approveJob,
    rejectJob,
    deleteAllJobs,
    clearError,
  };
}
